import { onMounted, onUnmounted } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useBannersCacheHandlers } from 'src/composables/banner/query/cache-handlers'
import { applyScanCategoryOverride } from 'src/composables/scan/query/useScanCategoryCache'
import { syncScanHistoryFromStatus } from 'src/composables/scans/query/useScanHistoryCache'
import { broadcastService } from 'src/services/app/broadcast.service'
import { log } from 'src/services/app/log.service'
import { BROADCAST_TOPICS, BROADCAST_UPDATE_TYPES } from 'src/services/consts/app.const'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { useAuthStore } from 'src/stores/auth'
import { useScanRegistryStore } from 'src/stores/scans/registry'

/**
 * Sets up app broadcast subscriptions for cross-tab sync. Call once in the
 * app root (e.g. AppIndex). Cleans up on unmount.
 *
 * Two top-level topics:
 *  - LOGOUT: another tab logged out → tear down session here too.
 *  - DATA_SYNC: generic cache/data sync bucket. The receiver below switches
 *    on `payload.updateType` (a value from `BROADCAST_UPDATE_TYPES`).
 *
 * Default DATA_SYNC flow — `QUERY_DATA`: mirror `{ queryKey, data }` into
 * this tab's Vue Query cache via `setQueryData`. Callers post this type via
 * `broadcastQueryData` (or a thin wrapper like `broadcastAccountData`). Add a
 * new `updateType` only when sync needs more than one cache operation
 * (e.g. `SCAN_STATUS` mirrors scan status and syncs history; `BANNER`
 * runs cache-handler cascades; `BANNER_REMOVE` replaces the
 * list, evicts site caches, and dismisses scan indicators).
 */
export function useBroadcastChannel() {
  const authStore = useAuthStore()
  const queryClient = useQueryClient()
  const scanRegistryStore = useScanRegistryStore()
  const { setBannerInCaches, evictBannerSiteCaches } = useBannersCacheHandlers()

  const unsubscribes = []

  onMounted(() => {
    subscribeTo(BROADCAST_TOPICS.LOGOUT, () => {
      log.info('[Broadcast] Received logout from other tab')
      authStore.logout({ fromOtherTab: true })
    })

    subscribeTo(BROADCAST_TOPICS.DATA_SYNC, (payload) => {
      if (!payload?.updateType) return
      log.info('[Broadcast] Received data-sync from other tab:', payload.updateType, { payload })
      switch (payload.updateType) {
        case BROADCAST_UPDATE_TYPES.QUERY_DATA:
          if (!Array.isArray(payload.queryKey) || payload.data === undefined) break
          queryClient.setQueryData(payload.queryKey, payload.data)
          break
        case BROADCAST_UPDATE_TYPES.SCAN_STATUS:
          if (!payload.siteId || payload.data === undefined) break
          queryClient.setQueryData([QUERY_KEYS.SCAN_STATUS, payload.siteId], payload.data)
          syncScanHistoryFromStatus(queryClient, payload.siteId, payload.data)
          break
        case BROADCAST_UPDATE_TYPES.SCAN_REGISTRY:
          scanRegistryStore.applyScanRegistryBroadcast(payload)
          break
        case BROADCAST_UPDATE_TYPES.SCAN_CATEGORY_OVERRIDE:
          if (!payload.siteId || !payload.updatedResults) break
          applyScanCategoryOverride(queryClient, payload.siteId, payload.updatedResults)
          break
        case BROADCAST_UPDATE_TYPES.BANNER:
          if (!payload.siteId || !payload.banner) break
          setBannerInCaches(payload.siteId, payload.banner)
          break
        case BROADCAST_UPDATE_TYPES.BANNER_REMOVE:
          if (!payload.siteId || !Array.isArray(payload.banners)) break
          queryClient.setQueryData([QUERY_KEYS.BANNERS_LIST], payload.banners)
          evictBannerSiteCaches(payload.siteId)
          scanRegistryStore.dismissScan(payload.siteId)
          break
      }
    })

    log.info('[Broadcast] Subscriptions registered')
  })

  onUnmounted(() => {
    unsubscribes.forEach((fn) => fn())
    broadcastService.closeChannel()
  })

  function subscribeTo(event, callback) {
    const unsubscribe = broadcastService.subscribe(event, callback)
    unsubscribes.push(unsubscribe)
    return unsubscribe
  }
}

/**
 * Generic cross-tab cache sync — posts `queryKey` + `data` so other tabs
 * can `setQueryData` without re-fetching. Use for simple set-only sync
 * (e.g. query fetches). For entity sync that needs cascading cache updates,
 * prefer semantic helpers (`broadcastBanner`,
 * `broadcastScanStatusData`).
 */
export function broadcastQueryData(queryKey, data) {
  log.info('[Broadcast] Sending query data', { queryKey })
  broadcastService.post(BROADCAST_TOPICS.DATA_SYNC, {
    updateType: BROADCAST_UPDATE_TYPES.QUERY_DATA,
    queryKey,
    data,
  })
}

/**
 * Broadcast the account/workspace payload so other tabs can mirror their
 * `ACCOUNT_DATA` query cache without re-fetching. Called from
 * `useAccountQuery`'s `queryFn` after a successful load.
 */
export function broadcastAccountData(accountData) {
  broadcastQueryData([QUERY_KEYS.ACCOUNT_DATA], accountData)
}

/**
 * Broadcast scan status payload so other tabs can mirror their
 * `[SCAN_STATUS, siteId]` query cache without re-fetching.
 *
 * @param {string} siteId
 * @param {import('src/services/helpers/scan/status.helper').ScanStatus} data
 */
export function broadcastScanStatusData(siteId, data) {
  log.info('[Broadcast] Sending scan status data', { siteId })
  broadcastService.post(BROADCAST_TOPICS.DATA_SYNC, {
    updateType: BROADCAST_UPDATE_TYPES.SCAN_STATUS,
    siteId,
    data,
  })
}

/**
 * Semantic entity sync for a banner — other tabs run the banner
 * cache-handler cascade (detail + list upsert).
 *
 * @param {string} siteId
 * @param {import('src/services/helpers/banner/detail.helper').BannerDetail} banner
 */
export function broadcastBanner(siteId, banner) {
  if (!siteId || !banner) return
  log.info('[Broadcast] Sending banner update', { siteId })
  broadcastService.post(BROADCAST_TOPICS.DATA_SYNC, {
    updateType: BROADCAST_UPDATE_TYPES.BANNER,
    siteId,
    banner,
  })
}

/**
 * Semantic banner delete sync — other tabs replace the banners list,
 * evict per-site caches, and dismiss any active scan for the site.
 *
 * @param {string} siteId
 * @param {import('src/services/helpers/banners/list.helper').BannerListItem[]} banners
 */
export function broadcastBannerRemove(siteId, banners) {
  if (!siteId || !Array.isArray(banners)) return
  log.info('[Broadcast] Sending banner remove', { siteId })
  broadcastService.post(BROADCAST_TOPICS.DATA_SYNC, {
    updateType: BROADCAST_UPDATE_TYPES.BANNER_REMOVE,
    siteId,
    banners,
  })
}

/**
 * Broadcast classification result so other tabs can sync scan status,
 * history, and banner denormalized counts from the PATCH response.
 *
 * @param {string} siteId
 * @param {import('src/services/helpers/scan/status.helper').ScanResults} updatedResults
 */
export function broadcastScanCategoryOverride(siteId, updatedResults) {
  if (!siteId || !updatedResults) return
  log.info('[Broadcast] Sending scan category override', { siteId })
  broadcastService.post(BROADCAST_TOPICS.DATA_SYNC, {
    updateType: BROADCAST_UPDATE_TYPES.SCAN_CATEGORY_OVERRIDE,
    siteId,
    updatedResults,
  })
}
